import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { apartmentsApi } from '../../api/apartmentsApi';
import { feeCategoriesApi, FeeCategory } from '../../api/feeCategoriesApi';
import { pdfExportsApi } from '../../api/pdfExportsApi';
import FeeCategoryMultiSelect from '../fee-categories/FeeCategoryMultiSelect';

interface ApartmentFeePdfExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  apartmentId: string;
  apartmentCode: string;
  buildingId: string;
  billingPeriods: string[];
}

const toApiBillingPeriod = (period: string): string => {
  if (period.includes('-')) return period;
  const [month, year] = period.split('/');
  return `${year}-${month.padStart(2, '0')}`;
};

const pollExportUntilReady = async (
  exportId: string,
  apartmentId: string,
  maxAttempts = 40,
): Promise<void> => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const exportRecord = await pdfExportsApi.getExport(exportId);
    if (exportRecord.status === 'completed') {
      const url = pdfExportsApi.getNoticeHtmlUrl(exportId, apartmentId, true);
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (exportRecord.status === 'failed') {
      throw new Error(exportRecord.errorMessage || 'Xuất PDF thất bại');
    }
    await new Promise((resolve) => setTimeout(resolve, 1500));
  }
  throw new Error('Hết thời gian chờ xuất PDF. Vui lòng thử lại sau.');
};

export const ApartmentFeePdfExportModal: React.FC<ApartmentFeePdfExportModalProps> = ({
  isOpen,
  onClose,
  apartmentId,
  apartmentCode,
  buildingId,
  billingPeriods,
}) => {
  const sortedPeriods = useMemo(
    () => Array.from(new Set(billingPeriods)).sort().reverse(),
    [billingPeriods],
  );
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [onlyOutstanding, setOnlyOutstanding] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [feeCategories, setFeeCategories] = useState<FeeCategory[]>([]);
  const [selectedFeeCategoryIds, setSelectedFeeCategoryIds] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && sortedPeriods.length > 0) {
      setSelectedPeriod(sortedPeriods[0]);
    }
  }, [isOpen, sortedPeriods]);

  useEffect(() => {
    if (!isOpen || !buildingId) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await feeCategoriesApi.getAll(buildingId);
        if (cancelled) return;
        const active = data.filter((cat) => cat.isActive !== false);
        setFeeCategories(active);
        setSelectedFeeCategoryIds(active.map((cat) => cat.id));
      } catch (error) {
        console.error('Error loading fee categories:', error);
        if (!cancelled) {
          setFeeCategories([]);
          setSelectedFeeCategoryIds([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, buildingId]);

  if (!isOpen) return null;

  const allCategoriesSelected =
    feeCategories.length === 0 ||
    selectedFeeCategoryIds.length === feeCategories.length;

  const handleExport = async () => {
    if (!selectedPeriod) {
      toast.error('Vui lòng chọn kỳ hóa đơn');
      return;
    }

    if (feeCategories.length > 0 && selectedFeeCategoryIds.length === 0) {
      toast.error('Vui lòng chọn ít nhất một danh mục phí');
      return;
    }

    setExporting(true);
    try {
      const response = await apartmentsApi.exportApartmentFeePdf(apartmentId, {
        billingPeriod: toApiBillingPeriod(selectedPeriod),
        onlyOutstanding,
        ...(!allCategoriesSelected && selectedFeeCategoryIds.length > 0
          ? { feeCategoryIds: selectedFeeCategoryIds }
          : {}),
      });
      toast.success('Đang tạo file PDF. Vui lòng chờ...');
      await pollExportUntilReady(response.id, apartmentId);
      toast.success('Đã mở file PDF phí dịch vụ');
      onClose();
    } catch (error: any) {
      console.error('Error exporting apartment fee PDF:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Xuất PDF thất bại');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Xuất file PDF - {apartmentCode}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {sortedPeriods.length === 0 ? (
            <p className="text-sm text-gray-500">Không có hóa đơn phí dịch vụ để xuất PDF.</p>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kỳ hóa đơn</label>
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  {sortedPeriods.map((period) => (
                    <option key={period} value={period}>
                      {period}
                    </option>
                  ))}
                </select>
              </div>

              <FeeCategoryMultiSelect
                categories={feeCategories.map((cat) => ({
                  id: cat.id,
                  name: cat.name,
                  feeCode: cat.feeCode,
                }))}
                selectedCategoryIds={selectedFeeCategoryIds}
                onChange={setSelectedFeeCategoryIds}
                disabled={feeCategories.length === 0}
              />

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={onlyOutstanding}
                  onChange={(e) => setOnlyOutstanding(e.target.checked)}
                />
                Chỉ xuất các hóa đơn chưa thanh toán
              </label>
            </>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Hủy
            </button>
            <button
              onClick={handleExport}
              disabled={exporting || sortedPeriods.length === 0}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded disabled:opacity-50"
            >
              {exporting ? 'Đang xuất...' : 'Xuất file PDF'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApartmentFeePdfExportModal;
